/**
 * Spec №11 (11.3.1) — App Shell declared LIST view renderer. Renders any Tier E
 * entity type's list from its ViewNodeConfig (columns/labels/format from
 * field_overrides, order from query.sorts) with zero per-entity code. Rows
 * deep-link to the four-plane record page.
 *
 * №13.1 (§7 / M31–M32) — each row exposes its cross-entity context actions via an
 * overflow "⋯" menu (e.g. Open matter / Run conflict check on a client, with the
 * client prefilled + locked). Every launch goes through the №11.1 registry
 * (`list_context_actions`) — no per-page prefill wiring (N16). The menu button
 * stops propagation so the row's click-to-navigate stays intact.
 */
import { useCallback, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import type { EntityTypeDefinition, ListFilters, ViewNodeConfig } from "../../../lib/types";
import { useEntities, useEntityType } from "../../../lib/hooks/use-kuvio";
import { mcpClient } from "../../../lib/mcp-client";
import { showToast } from "../../components/primitives";
import { formatCellValue } from "../../lib/v3-columns";
import {
  listColumns, applySort, filterSpecs, parseSortParam, serializeSort, defaultSortSpec,
} from "../../lib/declared-view";
import { ListToolbar } from "./ListToolbar";
import { useContextActions, atPlacement, type ResolvedAction } from "../../hooks/use-context-actions";
import type { AnchorInstance, InstancesForAnchor, WhatsNext } from "../../hooks/use-workflow";
import "./matter-process-columns.css";
/**
 * №16.2.1 (the "transfer") — the list process column-group is ON, built to the
 * locked mockup `16.2.1-matterlist-v1.html`: a leading needs-attention dot, then
 * Process · Stage · Progress · Next, plus five toolbar quick-filters. It is shown
 * only for workflow-anchor types (matters run workflows today). The panel and the
 * spine were the two run surfaces David validated; this is the third, now brought
 * up to a signed-off mockup rather than re-presented free-hand.
 */
const LIST_PROCESS_COLUMN = true;

// №16.2 — entity types that are workflow anchors (get the process column-group).
const WORKFLOW_ANCHOR_TYPES = new Set(["matter"]);

// №16.2.1 — the five process quick-filters (mockup toolbar). Single-select, URL-
// backed via `?pf=`; "open" is the default (no narrowing) and clears the param.
const PROCESS_FILTERS: { key: string; label: string }[] = [
  { key: "open", label: "Open" },
  { key: "attention", label: "Needs attention" },
  { key: "waiting", label: "Waiting" },
  { key: "decisions", label: "Decisions due" },
  { key: "noworkflow", label: "No workflow" },
];

import { VERB_FORMS, synthesisedCreateSlug } from "./verb-forms";
import { VerbLaunchModal } from "./VerbLaunchModal";
import { CreateEntityModal } from "./CreateEntityModal";

// Reserved URL params the toolbar owns; a field filter never uses these keys.
// `pf` is the process quick-filter (matters only).
const RESERVED_PARAMS = new Set(["q", "sort", "pf"]);

/** Shared transport for the two per-row workflow reads. Query keys mirror
 *  `use-workflow.ts` exactly so the cache dedupes with the record-page panel and
 *  its `useAnchorWorkflows`/`useWhatsNext` hooks — a row already opened elsewhere
 *  is a cache hit here. */
async function wfRead<T>(name: string, args: Record<string, unknown>): Promise<T> {
  const r = await mcpClient.toolsCall<T>(name, args);
  if (r.isError) throw new Error(r.content?.[0]?.text ?? `${name} failed`);
  return r.structuredContent as T;
}

/** The per-row process projection the mockup's cells + the quick-filters read. */
interface MatterProc {
  loading: boolean;
  hasWorkflow: boolean;
  processName: string;
  stage: string;
  concurrent: number;     // openSegmentCount — ">1" renders "◎ N stages open"
  isDecision: boolean;    // frontier is a client/agent decision → ◇ marker
  done: number; total: number; pct: number;
  nextTitle: string;
  nextTone: "ontrack" | "waiting" | "overdue" | "atrisk" | "none";
  dot: "none" | "warn" | "red";   // the ONE needs-attention token
  attention: string;      // raw summary token, kept for the quick-filters
  instanceNo?: number;
}

/**
 * Fold the anchor summary (frontier stage + progress + attention) and the driving
 * instance's `nextAction` (the ONE prioritised item) into the row projection.
 * `overdue`/`atRisk` only exist on `nextAction` (they need a pinned due date), so
 * the red/amber dot is derived here, not from the summary token alone.
 */
function buildProc(
  inst: AnchorInstance | undefined,
  next: WhatsNext["nextAction"],
  loading: boolean,
): MatterProc {
  if (!inst) {
    return {
      loading, hasWorkflow: false, processName: "—", stage: "—", concurrent: 0,
      isDecision: false, done: 0, total: 0, pct: 0,
      nextTitle: "Start a workflow…", nextTone: "none", dot: "none", attention: "none",
    };
  }
  const total = inst.stepsTotal ?? 0;
  const done = inst.stepsDone ?? 0;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const attention = inst.attention ?? "none";
  const overdue = !!next?.overdue;
  const atRisk = !!next?.atRisk;
  const dot: MatterProc["dot"] = overdue
    ? "red"
    : (atRisk || attention === "decision" || attention === "waiting") ? "warn" : "none";
  const nextTone: MatterProc["nextTone"] = overdue
    ? "overdue"
    : atRisk ? "atrisk" : attention === "waiting" ? "waiting" : "ontrack";
  return {
    loading, hasWorkflow: true,
    processName: inst.description || inst.templateCode,
    stage: inst.stage || "—",
    concurrent: inst.openSegmentCount ?? 0,
    isDecision: attention === "decision",
    done, total, pct,
    nextTitle: next?.title || inst.summary || "—",
    nextTone, dot, attention,
    instanceNo: inst.instanceNo,
  };
}

/** Client-side predicate for the five quick-filters. Non-matter rows always pass;
 *  a still-loading row is held out of a narrowing filter until its state is known
 *  (so a filtered view never flashes the wrong rows). */
function passesProcFilter(proc: MatterProc | null, pf: string): boolean {
  if (!proc || pf === "open") return true;
  if (proc.loading) return false;
  switch (pf) {
    case "attention": return proc.dot !== "none";
    case "waiting": return proc.attention === "waiting";
    case "decisions": return proc.attention === "decision";
    case "noworkflow": return !proc.hasWorkflow;
    default: return true;
  }
}

export function DeclaredListView({
  entityType,
  viewConfig,
}: {
  entityType: EntityTypeDefinition;
  viewConfig?: ViewNodeConfig;
}) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // №14.1.1-D (D-DS5) — the declared-view toolbar. Search + option/status/
  // reference filters are pushed to the server (bounded `list_*`); sort re-orders
  // the returned page. All state lives in the URL so a filtered list is shareable
  // and the back button steps through filter changes.
  const specs = filterSpecs(entityType, viewConfig).filter((s) => !RESERVED_PARAMS.has(s.field));
  const search = params.get("q") ?? "";
  const sortSpec = parseSortParam(params.get("sort")) ?? defaultSortSpec(viewConfig);
  const activeFilters: Record<string, string> = {};
  for (const s of specs) {
    const v = params.get(s.field);
    if (v) activeFilters[s.field] = v;
  }
  const queryActive = !!search || Object.keys(activeFilters).length > 0;

  const listFilters: ListFilters = { ...activeFilters };
  if (search) listFilters.search = search;
  const entities = useEntities(
    entityType,
    Object.keys(listFilters).length ? listFilters : undefined,
    { keepPreviousData: true },
  );

  const columns = listColumns(entityType, viewConfig);
  const rows = applySort(entities.data?.entities ?? [], sortSpec);
  // D-DS5 / №14.1.1-B — the lifecycle column reads as a coloured pill, the same
  // vocabulary the record header and the hand-written lists already use.
  const statusField = entityType.workflow ? (entityType.workflow.field ?? "status") : undefined;
  // №16.2 — the process column-group (frontier stage + derived progress + the one
  // attention token). Shown only for workflow-anchor types (matters run workflows
  // today; extend this set as other types become anchors). Per-row data comes from
  // the enriched `instances_for` glance — a batch list-summary is the future
  // optimisation for very long lists (this is one cached read per visible row).
  // №16.2.1-A (D-RN7) — parked with the other two run surfaces until the recovery
  // pack is validated; the column code and its projection feed stay wired.
  const showProcess = LIST_PROCESS_COLUMN && WORKFLOW_ANCHOR_TYPES.has(entityType.slug);

  const setSearch = useCallback((value: string) => {
    setParams((prev) => {
      const n = new URLSearchParams(prev);
      if (value) n.set("q", value); else n.delete("q");
      return n;
    }, { replace: true }); // debounced typing shouldn't flood the back stack
  }, [setParams]);

  const setFilter = useCallback((field: string, value: string | null) => {
    setParams((prev) => {
      const n = new URLSearchParams(prev);
      if (value) n.set(field, value); else n.delete(field);
      return n;
    });
  }, [setParams]);

  const clearQuery = useCallback(() => {
    setParams((prev) => {
      const n = new URLSearchParams(prev);
      n.delete("q");
      // Collect-then-delete (URLSearchParams.forEach — no DOM.Iterable needed for
      // this tsconfig's lib) so mutating during iteration is safe.
      const drop: string[] = [];
      n.forEach((_v, k) => { if (!RESERVED_PARAMS.has(k)) drop.push(k); });
      for (const k of drop) n.delete(k);
      return n;
    });
  }, [setParams]);

  const toggleSort = useCallback((field: string) => {
    setParams((prev) => {
      const n = new URLSearchParams(prev);
      const cur = parseSortParam(prev.get("sort")) ?? defaultSortSpec(viewConfig);
      const dir = cur && cur.field === field && cur.dir === "asc" ? "desc" : "asc";
      n.set("sort", serializeSort({ field, dir }));
      return n;
    });
  }, [setParams, viewConfig]);

  // №16.2.1 — the process column-group's data. Data-fetching tradeoff: the mockup
  // shows the REAL next-action title, which lives on `workflow_whats_next`, not on
  // the anchor glance. Rather than N per-row `useWhatsNext`/`useAnchorWorkflows`
  // component fetches (which would also hide the state the quick-filters need from
  // the parent), both reads are hoisted here as ONE `useQueries` each — the anchor
  // summaries first, then `whats_next` for each driving instance. That is two
  // cached reads per visible matter row (deduped against the record-page panel via
  // identical query keys, staleTime 10s). A single batch list-summary is the
  // future optimisation for very long lists; today the process column is matters-
  // only and the reads are cheap and cached. Non-matter lists issue zero queries.
  const procRows = showProcess ? rows : [];
  const summaryQueries = useQueries({
    queries: procRows.map((r) => {
      const id = String(r.id);
      return {
        queryKey: ["workflow", "instances-for", entityType.slug, id],
        enabled: showProcess,
        staleTime: 10_000,
        queryFn: () => wfRead<InstancesForAnchor>("workflow_instances_for", { subject_no: id, subject_type: entityType.slug }),
      };
    }),
  });
  const drivingNos = summaryQueries.map((q) => {
    const insts = q.data?.instances ?? [];
    return (insts.find((x) => x.status === "active") ?? insts[0])?.instanceNo;
  });
  const nextQueries = useQueries({
    queries: drivingNos.map((no) => ({
      queryKey: ["workflow", "whats-next", no],
      enabled: no != null,
      staleTime: 10_000,
      queryFn: () => wfRead<WhatsNext>("workflow_whats_next", { instance_no: no }),
    })),
  });
  // Fold each visible row into { row, proc }; index alignment holds because every
  // array here is derived from the same `rows` in this render.
  const decorated = rows.map((row, i) => {
    if (!showProcess) return { row, proc: null as MatterProc | null };
    const sq = summaryQueries[i];
    const insts = sq?.data?.instances ?? [];
    const inst = insts.find((x) => x.status === "active") ?? insts[0];
    const nq = nextQueries[i];
    const loading = (sq?.isLoading ?? false) || (inst != null && (nq?.isLoading ?? false));
    return { row, proc: buildProc(inst, nq?.data?.nextAction, loading) };
  });
  const procFilter = params.get("pf") ?? "open";
  const visibleRows = decorated.filter(({ proc }) => passesProcFilter(proc, procFilter));

  const setProcFilter = useCallback((f: string) => {
    setParams((prev) => {
      const n = new URLSearchParams(prev);
      if (f === "open") n.delete("pf"); else n.set("pf", f);
      return n;
    });
  }, [setParams]);

  // Next → the anchored full view (Drive lens), per the mockup.
  const openProcess = useCallback((id: string) => {
    navigate(`/app/place/${entityType.slug}/${id}/process?lens=drive`);
  }, [navigate, entityType.slug]);

  if (entities.isLoading) {
    return (
      <div className="card card--flush">
        <div className="skeleton">
          <div className="sk-line" style={{ width: "40%" }} />
          <div className="sk-line" style={{ width: "70%" }} />
          <div className="sk-line" style={{ width: "55%" }} />
        </div>
      </div>
    );
  }
  // A load error with NO active query is the bare error state. When a query IS
  // active, the error renders inside the card BELOW the toolbar (below) so the
  // user can still see and clear the search/filter that failed.
  if (entities.isError && !queryActive) {
    return (
      <div className="error-state">
        <h3>Couldn't load {entityType.name.toLowerCase()}</h3>
        <p>{String((entities.error as Error)?.message ?? "Unknown error")}</p>
      </div>
    );
  }
  // Pristine empty place (nothing here yet) — no toolbar, just the invitation.
  if (rows.length === 0 && !queryActive && !entities.isError) {
    return (
      <div className="empty-state">
        <h3>No {(entityType.plural ?? entityType.name).toLowerCase()} yet</h3>
        <p>Use “New {entityType.name.toLowerCase()}” above, or ask in the command bar.</p>
      </div>
    );
  }

  return (
    // Wide declared views (many columns) scroll horizontally WITHIN the card
    // instead of pushing the layout out. The row menu is fixed-positioned, so the
    // scroller never clips it.
    <div className="card card--flush">
      <ListToolbar
        entityName={entityType.plural ?? entityType.name}
        filters={specs}
        search={search}
        activeFilters={activeFilters}
        onSearch={setSearch}
        onFilter={setFilter}
      />
      {showProcess && !entities.isError && rows.length > 0 ? (
        // №16.2.1 — the process quick-filters (matters only). Canonical `.chip`
        // component; single-select, mirrors `?pf=`.
        <div className="list-toolbar list-toolbar--proc" role="group" aria-label="Filter by process state">
          {PROCESS_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`chip${procFilter === f.key ? " is-on" : ""}`}
              aria-pressed={procFilter === f.key}
              onClick={() => setProcFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      ) : null}
      {entities.isError ? (
        // A filtered/searched request that failed — keep the toolbar so the user
        // can clear the query that broke, not just a dead-end error.
        <div className="card__b">
          <div className="error-state">
            <h3>Couldn't load {entityType.name.toLowerCase()}</h3>
            <p>{String((entities.error as Error)?.message ?? "Unknown error")}</p>
            <button className="btn btn--secondary" onClick={clearQuery}>Clear filters</button>
          </div>
        </div>
      ) : rows.length === 0 ? (
        // "No results for this filter" — distinct from the pristine empty state.
        <div className="card__b">
          <div className="empty-state">
            <h3>No matches</h3>
            <p>No {(entityType.plural ?? entityType.name).toLowerCase()} match your search or filters.</p>
            <button className="btn btn--secondary" onClick={clearQuery}>Clear filters</button>
          </div>
        </div>
      ) : showProcess && visibleRows.length === 0 ? (
        // The process quick-filter narrowed everything away — offer a way back.
        <div className="card__b">
          <div className="empty-state">
            <h3>No matches</h3>
            <p>No {(entityType.plural ?? entityType.name).toLowerCase()} match the “{PROCESS_FILTERS.find((f) => f.key === procFilter)?.label ?? procFilter}” filter.</p>
            <button className="btn btn--secondary" onClick={() => setProcFilter("open")}>Show all</button>
          </div>
        </div>
      ) : (
        <div className="table-scroll">
          <table className="listgrid">
            <thead>
              <tr>
                {showProcess ? <th className="mproc-att-h" aria-label="Needs attention" /> : null}
                {columns.map((c) => {
                  const sortable = c.type !== "reference" && c.type !== "references";
                  const isSorted = sortSpec?.field === c.field;
                  const cls = [c.align === "right" ? "num" : "", sortable ? "sortable" : ""]
                    .filter(Boolean).join(" ");
                  return (
                    <th
                      key={c.field}
                      className={cls || undefined}
                      onClick={sortable ? () => toggleSort(c.field) : undefined}
                      aria-sort={isSorted ? (sortSpec!.dir === "asc" ? "ascending" : "descending") : undefined}
                    >
                      {c.label}
                      {sortable && isSorted && (
                        <span className="sortarrow">{sortSpec!.dir === "asc" ? "▴" : "▾"}</span>
                      )}
                    </th>
                  );
                })}
                {showProcess ? <th style={{ width: 200 }}>Process</th> : null}
                {showProcess ? <th style={{ width: 150 }}>Stage</th> : null}
                {showProcess ? <th style={{ width: 150 }}>Progress</th> : null}
                {showProcess ? <th style={{ width: 220 }}>Next</th> : null}
                <th aria-label="Actions" style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(({ row, proc }) => (
                <tr key={row.id} onClick={() => navigate(`/app/place/${entityType.slug}/${row.id}`)}>
                  {showProcess ? (
                    <td className="mproc-att">
                      {proc && proc.dot !== "none"
                        ? <span className={`mproc-dot mproc-dot--${proc.dot}`} aria-label={proc.dot === "red" ? "Overdue" : "Needs attention"} />
                        : null}
                    </td>
                  ) : null}
                  {columns.map((c) => {
                    const raw = (row as Record<string, unknown>)[c.field];
                    const isStatus = !!statusField && c.field === statusField && !!raw;
                    return (
                      <td
                        key={c.field}
                        className={`${c.align === "right" ? "num " : ""}${c.isTitle ? "primary " : ""}${c.monospace ? "mono" : ""}`.trim() || undefined}
                      >
                        {isStatus
                          ? <span className={`pill pill--${String(raw)}`}><span className="dot" />{String(raw)}</span>
                          : formatCellValue(raw, c)}
                      </td>
                    );
                  })}
                  {showProcess && proc ? <ProcessCells proc={proc} matterId={String(row.id)} onOpenProcess={openProcess} /> : null}
                  <td className="rowactions" onClick={(e) => e.stopPropagation()}>
                    <RowActionsMenu context={entityType.slug} id={row.id} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * №16.2.1 — the four process cells (mockup grammar): Process · Stage · Progress ·
 * Next. Pure renderer — the parent hoists the two reads (anchor summary + the
 * driving instance's `nextAction`) so the quick-filters can read the same derived
 * state. The progress bar reuses the canonical `.wf-prog` component. Next → the
 * anchored full view (Drive lens); it stops row-navigation so the click lands on
 * the process view, not the record home.
 */
function ProcessCells({
  proc,
  matterId,
  onOpenProcess,
}: {
  proc: MatterProc;
  matterId: string;
  onOpenProcess: (id: string) => void;
}) {
  const goNext = (e: React.MouseEvent) => { e.stopPropagation(); onOpenProcess(matterId); };

  if (proc.loading && !proc.hasWorkflow) {
    return (
      <>
        <td className="mproc-cell mproc-cell--none">…</td>
        <td className="mproc-stage mproc-stage--none">…</td>
        <td className="mproc-prog mproc-prog--none">…</td>
        <td className="mproc-next mproc-next--none">…</td>
      </>
    );
  }
  if (!proc.hasWorkflow) {
    // A matter with no running workflow — the quiet "—" plus a start affordance.
    return (
      <>
        <td className="mproc-cell mproc-cell--none">—</td>
        <td className="mproc-stage mproc-stage--none">—</td>
        <td className="mproc-prog mproc-prog--none">—</td>
        <td className="mproc-next">
          <button type="button" className="mproc-next__link mproc-next--none" onClick={goNext}>
            Start a workflow…
          </button>
        </td>
      </>
    );
  }
  return (
    <>
      <td className="mproc-cell" title={proc.processName}>{proc.processName}</td>
      <td className="mproc-stage">
        {proc.concurrent > 1 ? (
          <><span className="mproc-stage__con">◎ {proc.concurrent}</span> stages open</>
        ) : (
          <span className={proc.isDecision ? "mproc-stage__dec" : undefined}>{proc.stage}</span>
        )}
      </td>
      <td className="mproc-prog">
        <span className="wf-prog">
          <span className="wf-prog__track"><span className="wf-prog__fill" style={{ width: `${proc.pct}%` }} /></span>
          <span className="wf-prog__n">{proc.done} / {proc.total}</span>
        </span>
      </td>
      <td className="mproc-next">
        <button
          type="button"
          className={`mproc-next__link mproc-next--${proc.nextTone}`}
          onClick={goNext}
          title={proc.nextTitle}
        >
          {proc.nextTitle}
        </button>
      </td>
    </>
  );
}

/**
 * №13.1 (§7) — the per-row overflow menu. Reads the same №11.1 registry the
 * `ContextActionBar` uses (prefill + locked resolved server-side) and dispatches
 * with the identical verb-shape switch: synthesised `create_<slug>` →
 * `CreateEntityModal`; a form-backed first-party verb → `VerbLaunchModal`;
 * otherwise a read/idempotent verb is invoked directly. The popup is fixed-
 * positioned off the button so the table's overflow never clips it.
 */
function RowActionsMenu({ context, id }: { context: string; id: string }) {
  const qc = useQueryClient();
  const actions = useContextActions(context, id);
  const rows = atPlacement(actions.data?.actions, "record_actions");

  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const [launch, setLaunch] = useState<ResolvedAction | null>(null);
  const [createFor, setCreateFor] = useState<{ action: ResolvedAction; slug: string } | null>(null);
  const createType = useEntityType(createFor?.slug);

  async function run(action: ResolvedAction) {
    setPos(null);
    const createSlug = synthesisedCreateSlug(action.verb);
    if (createSlug) {
      setCreateFor({ action, slug: createSlug });
      return;
    }
    if (VERB_FORMS[action.verb]) {
      setLaunch(action);
      return;
    }
    try {
      const r = await mcpClient.toolsCall(action.verb, action.prefill);
      if (r.isError) throw new Error(r.content?.[0]?.text ?? `${action.verb} failed`);
      showToast(`${action.label} done`, "success");
      void qc.invalidateQueries({ queryKey: ["context-actions"] });
    } catch (e) {
      showToast((e as Error).message, "error", true);
    }
  }

  function toggle(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    if (pos) {
      setPos(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  }

  if (rows.length === 0) return null;

  return (
    <>
      {/* №14.1.1-B — the row menu was the last fully inline-styled component in
          the App Shell (raw px, raw 0.5px borders, console tokens). It now
          renders from the one component layer: `.rowmenu-btn` + `.rowmenu`. */}
      <button
        type="button"
        className="rowmenu-btn"
        aria-label="Row actions"
        aria-haspopup="menu"
        aria-expanded={!!pos}
        onClick={toggle}
      >
        ⋯
      </button>
      {pos && (
        <>
          <div
            className="rowmenu__scrim"
            onClick={(e) => {
              e.stopPropagation();
              setPos(null);
            }}
          />
          <div
            className="rowmenu"
            role="menu"
            onClick={(e) => e.stopPropagation()}
            style={{ top: pos.top, right: pos.right }}
          >
            {rows.map((a) => (
              <button key={`${a.verb}:${a.label}`} type="button" role="menuitem" onClick={() => void run(a)}>
                {a.label}
              </button>
            ))}
          </div>
        </>
      )}

      {launch && VERB_FORMS[launch.verb] && (
        <VerbLaunchModal
          action={launch}
          spec={VERB_FORMS[launch.verb]!}
          open={!!launch}
          onClose={() => setLaunch(null)}
          onDone={() => {
            setLaunch(null);
            void qc.invalidateQueries({ queryKey: ["context-actions"] });
          }}
        />
      )}

      {createFor && createType.data && (
        <CreateEntityModal
          entityType={createType.data}
          open={!!createFor}
          heading={createFor.action.label}
          lockedPrefill={createFor.action.prefill}
          onClose={() => setCreateFor(null)}
          onSaved={() => {
            setCreateFor(null);
            void qc.invalidateQueries({ queryKey: ["context-actions"] });
          }}
        />
      )}
    </>
  );
}
